// src/composer/share.ts
import { deflateRaw, inflateRaw } from 'pako';
import type { BuildState } from '../packs/types';

export interface SharePayload {
  packId: string;
  packVersion: number;
  build: BuildState;
  hue: number;
  accentHue: number;
  glowHue: number;
  detailHue: number;
  trimHue: number;
  backgroundHue: number;
  primarySat: number;
  accentSat: number;
  glowSat: number;
  detailSat: number;
  trimSat: number;
  bgSat: number;
  borderEnabled: boolean;
  borderColor: string;
}

interface WireFormat {
  p: string; v: number; b: BuildState;
  h: number; a: number; gw?: number; dh?: number; th?: number;
  bh?: number; bg?: string;
  sa?: number[];
  br: [1, string] | 0;
  n?: string; t?: string;
}

export function encodeShare(packId: string, packVersion: number, payload: Omit<SharePayload, 'packId' | 'packVersion'>): string {
  const wire: WireFormat = {
    p: packId, v: packVersion, b: payload.build,
    h: payload.hue, a: payload.accentHue, gw: payload.glowHue,
    dh: payload.detailHue, th: payload.trimHue,
    bh: payload.backgroundHue,
    sa: [payload.primarySat, payload.accentSat, payload.glowSat, payload.detailSat, payload.trimSat, payload.bgSat],
    br: payload.borderEnabled ? [1, payload.borderColor] : 0,
    n: payload.build.name ?? undefined,
    t: payload.build.tagline ?? undefined,
  };
  const json = JSON.stringify(wire);
  const bytes = new TextEncoder().encode(json);
  const compressed = deflateRaw(bytes);
  const b64 = btoa(Array.from(compressed, c => String.fromCharCode(c)).join(''));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function decodeShare(encoded: string): SharePayload | null {
  try {
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
    const decompressed = inflateRaw(bytes);
    const json = new TextDecoder().decode(decompressed);
    const wire: WireFormat = JSON.parse(json);
    const accentHue = wire.a;
    const hue = wire.h;
    return {
      packId: wire.p,
      packVersion: wire.v,
      build: { ...wire.b, name: wire.n, tagline: wire.t },
      hue,
      accentHue,
      glowHue: wire.gw ?? (accentHue + 180) % 360,
      detailHue: wire.dh ?? (hue + 200) % 360,
      trimHue: wire.th ?? 210,
      backgroundHue: wire.bh ?? 222,
      primarySat: wire.sa?.[0] ?? 100,
      accentSat: wire.sa?.[1] ?? 100,
      glowSat: wire.sa?.[2] ?? 100,
      detailSat: wire.sa?.[3] ?? 100,
      trimSat: wire.sa?.[4] ?? 100,
      bgSat: wire.sa?.[5] ?? 100,
      borderEnabled: wire.br !== 0,
      borderColor: Array.isArray(wire.br) ? wire.br[1] : '#000000',
    };
  } catch {
    return null;
  }
}

export function slugify(name: string | undefined, fallback = 'ship'): string {
  if (!name) return fallback;
  const s = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return s || fallback;
}
